import { FC, useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';

import { GMM_CANVAS_CLASS_NAME } from '../problemCanvas/problemCanvasDom';

import { MODAL_Z_INDEX } from './constants';
import { problemModalStore } from './problemModalStore';

function getAbsolutePosition(el: HTMLElement): { x: number; y: number } {
  const rect = el.getBoundingClientRect();

  return {
    x: rect.left + window.scrollX,
    y: rect.top + window.scrollY,
  };
}

export const NativeInputBox: FC = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const [location, setLocation] = problemModalStore(state => [
    state.nativeInputLocation,
    state.setNativeInputLocation,
  ]);
  const [text, setNativeInputText] = problemModalStore(state => [
    state.nativeInputText,
    state.setNativeInputText,
  ]);
  const startingCursorPosition = problemModalStore(
    state => state.nativeInputCursorOnMount
  );

  // Update canvas offset on scroll/resize
  const updateCanvasOffset = useCallback(() => {
    const canvas = document.querySelector(
      `.${GMM_CANVAS_CLASS_NAME}`
    ) as HTMLElement | null;

    if (canvas) {
      setCanvasOffset(getAbsolutePosition(canvas));
    }
  }, []);

  useEffect(() => {
    updateCanvasOffset();
    window.addEventListener('scroll', updateCanvasOffset, true);
    window.addEventListener('resize', updateCanvasOffset);

    return () => {
      window.removeEventListener('scroll', updateCanvasOffset, true);
      window.removeEventListener('resize', updateCanvasOffset);
    };
  }, [updateCanvasOffset]);

  useEffect(() => {
    const keydown = (evt: KeyboardEvent) => {
      if (!inputRef.current) return;

      if (evt.key === 'Escape' || evt.key === 'Enter') {
        setLocation(undefined);
        evt.preventDefault();
        evt.stopPropagation();
      }
    };

    const inputChanged = () => {
      if (!inputRef.current) return;
      setNativeInputText(inputRef.current.value);
    };

    if (inputRef.current) {
      inputRef.current.addEventListener('keydown', keydown);
      inputRef.current.addEventListener('input', inputChanged);
      inputRef.current.focus({ preventScroll: true });
      inputRef.current.setSelectionRange(
        startingCursorPosition,
        startingCursorPosition
      );
    }

    return () => {
      inputRef.current?.removeEventListener('keydown', keydown);
      inputRef.current?.removeEventListener('input', inputChanged);
    };
  }, [setLocation, setNativeInputText, startingCursorPosition]);

  if (!location) return null;

  const absLeft = canvasOffset.x + location.x;
  const absTop = canvasOffset.y + location.y;

  // Create a portal to render the input box outside of the React app's root
  // element, so it can overlay other elements properly.
  // Note: subtract 1 from zIndex so it appears below other modals (e.g. teacher messages)
  return createPortal(
    <div
      style={{
        position: 'absolute',
        left: `${absLeft}px`,
        top: `${absTop}px`,
        zIndex: MODAL_Z_INDEX - 1,
      }}
    >
      <input type="text" ref={inputRef} defaultValue={text} />
    </div>,
    document.body
  );
};
